import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import CouponInput from '../AdminDashboard/CouponInput'; // Add this import
import {
  FaCalculator,
  FaBrain,
  FaPenFancy,
  FaBook,
  FaHistory,
  FaChevronRight,
  FaChevronLeft,
  FaLock,
  FaCheckCircle
} from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const subjectConfig = {
  Maths: {
    icon: <FaCalculator />,
    color: "from-blue-500 to-teal-400",
    route: "/maths-topics"
  },
  ThinkingSkills: {
    icon: <FaBrain />,
    color: "from-purple-500 to-pink-400",
    route: "/thinking-skills"
  },
  Writing: {
    icon: <FaPenFancy />,
    color: "from-orange-500 to-yellow-400",
    route: "/writing-test"
  },
  Reading: {
    icon: <FaBook />,
    color: "from-teal-500 to-green-400",
    route: "/reading-test"
  }
};

const SubjectTestsPage = () => {
  const navigate = useNavigate();
  const [subjectsStatus, setSubjectsStatus] = useState([]);
  const [testHistory, setTestHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState(null);
  const [appliedCoupons, setAppliedCoupons] = useState({});

  // Update the fetchData function in SubjectTestsPage.js
  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);

        try {
          // Fetch subject access status
          const subjectResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student-access-status/${user.id}`
          );
          const subjectData = await subjectResponse.json();

          // Check for expired subscriptions (5 minutes)
          const updatedSubjectData = subjectData.map(subject => {
            if (subject.hasAccess && subject.expiry_date) {
              const expiryTime = new Date(subject.expiry_date);
              const currentTime = new Date();

              // Mark as expired if expiry date has passed
              if (currentTime > expiryTime) {
                console.log(`Subscription for ${subject.subject} expired at ${expiryTime}`);
                return {
                  ...subject,
                  hasAccess: false,
                  isApproved: false
                };
              } else {
                // Calculate remaining time in days
                const timeRemaining = expiryTime - currentTime;
                const daysRemaining = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
                console.log(`Subscription for ${subject.subject} expires in ${daysRemaining} days`);
              }
            }
            return subject;
          });

          setSubjectsStatus(updatedSubjectData);

          // Fetch test history
          const historyResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/auth/student/test-history/${user.id}`
          );
          const historyData = await historyResponse.json();

          const subjectHistory = historyData.filter(test =>
            test.testType &&
            test.testType.toLowerCase() !== 'mock' &&
            !['practice', 'weekly', 'mega'].includes(test.testType.toLowerCase())
          );
          setTestHistory(subjectHistory);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Set up interval to check for expiry every minute
    const intervalId = setInterval(() => {
      fetchData();
    }, 360000); // Check every minute

    return () => clearInterval(intervalId);
  }, []);

  const getSubjectStatus = (subjectData) => {
    // Check if subscription has expired
    if (subjectData.hasAccess && subjectData.expiry_date) {
      const expiryTime = new Date(subjectData.expiry_date);
      const currentTime = new Date();

      if (currentTime > expiryTime) {
        return "no-access"; // Expired
      }
    }

    if (subjectData.hasAccess) return "approved";
    return "no-access";
  };

  const handleSubjectPayment = async (subject, e) => {
    e.preventDefault();
    const stripe = await stripePromise;

    try {
      const endpoint = appliedCoupons[subject]
        ? "/api/payment/create-checkout-session-with-coupon"
        : "/api/payment/create-checkout-session";

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_id: studentId,
          subject,
          coupon_code: appliedCoupons[subject]?.id
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if it's an expiry-related error
        if (data.is_expired === false) {
          const expiryText = data.expiry_date
            ? new Date(data.expiry_date).toLocaleDateString()
            : "Unknown";

          alert(
            `You already have active access to ${subject}. Expiry date: ${expiryText}`
          );
          return;
        }
        throw new Error(data.error || "Payment failed");
      }

      // Use redirect method instead of checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        // Fallback to redirectToCheckout
        const result = await stripe.redirectToCheckout({
          sessionId: data.id
        });
        if (result.error) {
          alert(result.error.message);
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert(error.message);
    }
  };

  const formatTestDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  const handleStartTest = (subject) => {
    const subjectData = subjectsStatus.find(s => s.subject === subject);
    const status = getSubjectStatus(subjectData);
    const config = subjectConfig[subject] || subjectConfig.Reading;

    // Always allow access if status is 'approved' (which now includes pending approval)
    if (status === 'approved') {
      navigate(config.route);
    } else {
      alert(`You need to purchase access to ${subject} tests`);
    }
  };

  const checkSubscriptionExpiry = (subjectData) => {
    if (!subjectData?.expiry_date) {
      return {
        isExpired: true,
        daysRemaining: 0,
        expiryDate: null
      };
    }

    const expiryDate = new Date(subjectData.expiry_date);
    const currentDate = new Date();
    const timeRemaining = expiryDate - currentDate;
    const daysRemaining = Math.ceil(timeRemaining / (1000 * 60 * 60 * 24));

    return {
      isExpired: timeRemaining <= 0,
      daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
      expiryDate
    };
  };


  const renderSubjectButton = (subjectData) => {
    const status = getSubjectStatus(subjectData);
    const config = subjectConfig[subjectData.subject] || subjectConfig.Reading;
    const expiryInfo = checkSubscriptionExpiry(subjectData);

    // Show expiry warning for active subscriptions
    if (status === 'approved' && !expiryInfo.isExpired) {
      return (
        <div className="space-y-3">
          {expiryInfo.daysRemaining <= 7 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-yellow-800 text-sm font-medium">
                ⏳ Access expires in {expiryInfo.daysRemaining} day{expiryInfo.daysRemaining !== 1 ? 's' : ''}
              </p>
              <p className="text-yellow-600 text-xs mt-1">
                Renew now to continue uninterrupted access
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => handleStartTest(subjectData.subject)}
              className={`flex-1 py-2 sm:py-3 rounded-lg font-semibold text-white bg-gradient-to-br ${config.color} hover:opacity-90 text-sm sm:text-base`}
            >
              Start Test
            </button>

            <button
              onClick={(e) => handleSubjectPayment(subjectData.subject, e)}
              className="flex-1 py-2 sm:py-3 rounded-lg font-semibold bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 text-sm sm:text-base"
            >
              Renew Access
            </button>
          </div>
        </div>
      );
    } else if (expiryInfo.isExpired && subjectData.hasAccess) {
      // Show renewal option for expired subscriptions
      return (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm font-medium">
              ⚠️ Your access has expired on{" "}
              {expiryInfo.expiryDate instanceof Date
                ? expiryInfo.expiryDate.toLocaleDateString()
                : "an unknown date"}
            </p>
            <p className="text-red-600 text-xs mt-1">
              Renew now to regain access
            </p>
          </div>

          {/* Add Coupon Input for renewals */}
          <div className="mb-3">
            <CouponInput
              onCouponApplied={(coupon) => setAppliedCoupons(prev => ({ ...prev, [subjectData.subject]: coupon }))}
              onCouponRemoved={() => setAppliedCoupons(prev => {
                const newCoupons = { ...prev };
                delete newCoupons[subjectData.subject];
                return newCoupons;
              })}
              type="subject"
            />
          </div>

          <button
            onClick={(e) => handleSubjectPayment(subjectData.subject, e)}
            className="w-full py-2 sm:py-3 rounded-lg font-semibold bg-gradient-to-r from-green-500 to-teal-500 text-white hover:opacity-90 text-sm sm:text-base"
          >
            🔄 Renew {subjectData.subject} Access
          </button>
        </div>
      );
    } else {
      // New purchase
      return (
        <div>
          <div className="mb-3">
            <CouponInput
              onCouponApplied={(coupon) => setAppliedCoupons(prev => ({ ...prev, [subjectData.subject]: coupon }))}
              onCouponRemoved={() => setAppliedCoupons(prev => {
                const newCoupons = { ...prev };
                delete newCoupons[subjectData.subject];
                return newCoupons;
              })}
              type="subject"
            />
          </div>
          <button
            onClick={(e) => handleSubjectPayment(subjectData.subject, e)}
            className="w-full py-2 sm:py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:opacity-90 text-sm sm:text-base"
          >
            <FaLock className="inline mr-2" /> Unlock {subjectData.subject}
          </button>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-teal-50 min-h-screen py-8 sm:py-12 px-4 sm:px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 text-sm sm:text-base"
          >
            <FaChevronLeft className="mr-1" /> Back to Dashboard
          </button>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mt-3 sm:mt-4">Subject Tests</h2>
          <p className="text-gray-600 text-sm sm:text-base">Practice tests for each subject</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-12">
          {subjectsStatus.map((subjectData) => {
            const status = getSubjectStatus(subjectData);
            const config = subjectConfig[subjectData.subject] || subjectConfig.Reading;

            return (
              <div
                key={subjectData.subject}
                className={`p-[2px] rounded-xl sm:rounded-2xl bg-gradient-to-br ${config.color} shadow-md hover:shadow-lg transition`}
              >
                <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 h-full">
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div>
                      <div className={`inline-block px-2 py-1 sm:px-3 sm:py-1 text-xs rounded-full font-semibold mb-2 sm:mb-3 ${status === 'approved' ? 'bg-green-100 text-green-800' :
                        status === 'pending-approval' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                        {subjectData.subject}
                        {status === 'pending-approval' && ' (Pending)'}
                        {status === 'no-access' && ' (Locked)'}
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{subjectData.subject} Tests</h3>
                    </div>
                    <div className={`p-2 sm:p-3 rounded-full ${status === 'approved' ? 'bg-green-100' :
                      status === 'pending-approval' ? 'bg-yellow-100' :
                        'bg-gray-100'
                      }`}>
                      {config.icon}
                    </div>
                  </div>

                  <div className="mb-4 sm:mb-6">
                    <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-3">Practice tests for {subjectData.subject}:</p>
                    <ul className="space-y-1 sm:space-y-2">
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2 text-sm sm:text-base" />
                        <span className="text-sm sm:text-base">Topic-wise practice</span>
                      </li>
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2 text-sm sm:text-base" />
                        <span className="text-sm sm:text-base">Un-Timed tests</span>
                      </li>
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2 text-sm sm:text-base" />
                        <span className="text-sm sm:text-base">Badge tests</span>
                      </li>
                      <li className="flex items-center">
                        <FaCheckCircle className="text-green-500 mr-2 text-sm sm:text-base" />
                        <span className="text-sm sm:text-base">Detailed solutions</span>
                      </li>
                    </ul>
                  </div>

                  {renderSubjectButton(subjectData)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SubjectTestsPage;